import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BuddyLogo, LumiMascot } from "../components/Brand";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth, startGoogleLogin } from "../lib/auth";
import { homeForRole } from "../lib/roles";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(t("login.helloName", { name: u.full_name }));
      nav(homeForRole(u.role));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("login.invalid"));
    } finally {
      setBusy(false);
    }
  }

  async function demoLogin(role) {
    const map = {
      student: "elem_student@demo.com",
      parent: "parent@demo.com",
      teacher: "teacher@demo.com",
      school_admin: "school_admin@demo.com",
      platform_admin: "platform_admin@demo.com",
    };
    setEmail(map[role]);
    setPassword("demo123");
    setBusy(true);
    try {
      const u = await login(map[role], "demo123");
      toast.success(t("login.helloName", { name: u.full_name }));
      nav(homeForRole(u.role));
    } catch (err) {
      toast.error("Demo login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen student-bg flex flex-col">
      <header className="p-6 flex items-center justify-between"><Link to="/"><BuddyLogo /></Link><LanguageSwitcher /></header>
      <main className="flex-1 grid lg:grid-cols-2 gap-8 max-w-[1200px] mx-auto w-full px-6 py-8 items-center">
        <div className="hidden lg:flex flex-col items-center">
          <LumiMascot size={200} />
          <p className="mt-4 font-student font-bold text-2xl text-slate-900">{t("login.welcomeBack")}</p>
          <p className="text-slate-600">{t("login.keepLearning")}</p>
        </div>
        <div className="hb-card p-8 hb-chunky-shadow">
          <h1 className="font-adult font-bold text-3xl text-slate-900">{t("login.title")}</h1>
          <p className="text-sm text-slate-600 mt-1">{t("login.subtitle")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">{t("common.email")}</Label>
              <Input id="email" type="email" required autoComplete="email"
                     data-testid="login-email"
                     value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="password">{t("common.password")}</Label>
              <Input id="password" type="password" required autoComplete="current-password"
                     data-testid="login-password"
                     value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6">
              {busy ? t("login.signingIn") : t("common.signIn")}
            </Button>
          </form>

          <div className="mt-4 flex items-center gap-3">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{t("login.orContinue")}</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>
          <button
            type="button"
            onClick={startGoogleLogin}
            data-testid="login-google"
            className="mt-3 w-full flex items-center justify-center gap-3 border border-slate-300 rounded-full py-3
                       bg-white hover:bg-slate-50 text-slate-800 font-medium transition-colors"
            aria-label={t("login.continueWithGoogle")}
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.62l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            {t("login.continueWithGoogle")}
          </button>

          <div className="text-xs text-center mt-4 text-slate-500">
            {t("login.noAccount")}{" "}
            <Link to="/register" className="text-blue-600 font-medium" data-testid="login-link-register">{t("login.createOne")}</Link>
          </div>
          <div className="mt-6 border-t border-slate-200 pt-4">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold text-center">{t("login.tryDemo")}</div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              {["student","parent","teacher","school_admin","platform_admin"].map((r) => (
                <Button key={r} variant="outline" size="sm"
                        data-testid={`demo-login-${r}`}
                        onClick={() => demoLogin(r)}>
                  {t(`roles.${r}`)}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
